/**
 * @file        make-data-uri.js
 * @author      Eddie Roosenmaallen, eddie@kingsds.netework
 * @date        Oct 2020
 */

const { leafMerge } = require('dcp/utils/obj-merge');
const { rehydrateRange } = require("dcp/dcp-client/range-object");
const { encodeDataURI, generateOpaqueId } = require("dcp/utils");
const { sha256 } = require('dcp/dcp-client/wallet/keystore')._internalEth.util;
const tokenParseRe = /([^{]+)({([^:}]+)(:([0-9-]+)(,([0-9-]+))?)?})?/g /* sorry */;

/**
 * Expand storage details, so that parameter tokens are evaluated and substituted into the pattern string.
 *
 * Syntax: parameter names are enclosed in curly braces.  If the parameter name is followed by a colon,
 *         the remainder is treated as argument(s) to String.slice, and the parameter value is sliced
 *         before being inserted into the storageDetails string.  Arguments are separated by commas,
 *         spaces are not allowed, negative numbers are okay.  e.g.  {job:1,-2} expands to "bcde" if the
 *         parameter object is { job: "abcdefgh" }.
 *
 * Two magic parameters are intrinsically supported: hash and unique.  Unique produces a unique string.
 * Hash is the hash of the parameters.  Unique and hash are only evaluated once per call, no matter how
 * many times they are used within storageDetails.
 *
 * @param {string} storageDetails       a string like   '/path/to/storage/{job:1,2}/{job}/{hash}-data.kvin'
 * @param {object} parameters           an object with properties that are parameter names and values that
 *                                      are expanded parameter values.
 * @returns {string}
 */
function expandStorageDetails(storageDetails, parameters)
{
  var expanded = '';
  var originalParameters = parameters;

  if (storageDetails.indexOf('{hash}') !== -1)
  {
    const hash = sha256(Buffer.from(JSON.stringify(originalParameters))).toString('hex');
    if (parameters === originalParameters)
      parameters = Object.assign({}, parameters);
    parameters.hash = hash;
  }

  if (storageDetails.indexOf('{unique}') !== -1)
  {
    const unique = generateOpaqueId();
    if (parameters === originalParameters)
      parameters = Object.assign({}, parameters);
    parameters.unique = unique;
  }

  storageDetails.matchAll(tokenParseRe).forEach(matches => {
    const plain = matches[1];
    const token = matches[3];
    const start = matches[5];
    const end   = matches[7];
    let param = token ? parameters[token] || `missing-${token}-` : '';

    if (typeof start !== 'undefined')
      param = param.slice(start, end);
    expanded += plain + param;
  });

  return expanded;
}

/** 
 * Make a URI for the given the storage type and details.
 * @param {string}  storageType     The storage type (range|pattern)
 * @param {string}  storageDetails  Details about the storage and/or data, which
 *                                  varies based on storageType.
 *                  - range → storageDetails is a range descriptor. The 'slice' parameter is used to specify
 *                    element of the range to create the URL for.
 *                  - pattern → storageDetails is a pattern to form a URI-encoded URI that can be fetched
 *                    by the supervisor, created by substituting parameters into the pattern.
 * @param {object}  parameters      An object used for making substitutions in URIs. Each object key is
 *                                  treated as a label which can appear between curly braces in the pattern;
 *                                  the corresponding value is then substituted into the string.
 *
 *                                  A special key, hash, is used to indicate that the substituted value
 *                                  should be the the result of calculating the hash of all the other values,
 *                                  appended together in alphabetical order of their keys.
 *
 *                                  Another special key, unique, is used to generate a random string of
 *                                  characters which is probably unique.
 *
 *                                  Both hash and unique are lazily-generated to avoid overhead. This
 *                                  routine guarantees that parameters will not be mutated.
 *
 * @returns {string} the slice URI. This could be any URI suitable for the fetchURI function, including
 *                   http:, https: and data: URIs.
 */
exports.makeValueURI = function makeValueURI(storageType, storageDetails, parameters) {
  if (!parameters || !(typeof parameters === 'object')) {
    throw new Error('parameters object is required');
  }

  if (!storageDetails) {
    throw new Error('storageDetails is required');
  }

  switch (storageType) {
    case 'range': {
      const ro = rehydrateRange(storageDetails);
      if (!parameters.slice) {
        throw new Error('Slice parameter is required for range storageType');
      }

      // -1 to prevent an OBOE since slice numbers start at 1.
      return encodeDataURI(ro[parameters.slice - 1]);
    }
    case 'pattern':
      return expandStorageDetails(storageDetails, parameters);
    default:
      throw new Error(`Unknown storageType parameter "${storageType}"`);
  }
}

/**
 * Factory function which returns the curated parameters associated with a given given job/slice; these
 * patterns are used for the makeValueURI etc function to expand a URI pattern into a URI. See DCP-1480.
 *
 * @param   {object}  job             The jobs table row corresponding to the job
 * @param   {object}  extraParams     An object with extra parameters (eg sliceNumber) to add to the 
 *                                    returned value.
 */
 exports.makePatternParams = function utils$$makePatternParams(job, extraParams)
 {
   return leafMerge({
     job:     job.opaqueId,
   }, extraParams);
 }
